import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { replyEmail, forwardEmail, deleteMessage, moveMessage, flagMessage, markRead, categorizeMessage, getAttachments, getChildFolders } from '../api';
import Compose from './Compose';
import { colorByName } from '../categories';

function FolderPicker({ folders, onPick }) {
  const [stack, setStack] = useState([{ label: 'Folders', items: folders }]);
  const [search, setSearch] = useState('');
  const [children, setChildren] = useState({});

  const current = stack[stack.length - 1];

  async function drillInto(folder) {
    if (folder.childFolderCount > 0) {
      if (!children[folder.id]) {
        try {
          const data = await getChildFolders(folder.id);
          setChildren(prev => ({ ...prev, [folder.id]: data }));
          setStack(prev => [...prev, { label: folder.displayName, items: data, folderId: folder.id }]);
        } catch {}
      } else {
        setStack(prev => [...prev, { label: folder.displayName, items: children[folder.id], folderId: folder.id }]);
      }
      setSearch('');
    } else {
      onPick(folder.id);
    }
  }

  const filtered = current.items.filter(f =>
    f.displayName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <input
        className="dropdown-search"
        placeholder="Search…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        autoFocus
      />
      {stack.length > 1 && (
        <button className="folder-picker-back" onClick={() => { setStack(s => s.slice(0, -1)); setSearch(''); }}>
          ◂ {stack[stack.length - 2].label}
        </button>
      )}
      <div className="dropdown-items folder-picker-items">
        {filtered.map(f => (
          <button key={f.id} className="folder-picker-row" onClick={() => drillInto(f)}>
            <span>{f.displayName}</span>
            <span className="folder-picker-actions">
              <span className="folder-pick-btn" onClick={e => { e.stopPropagation(); onPick(f.id); }}>Move here</span>
              {f.childFolderCount > 0 && <span className="folder-chevron-right">▸</span>}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function EmailView({ message, folders, categoryMap, allCategories, pinnedIds = [], onTogglePin, signature, onDeleted, onMoved, onMessageUpdate }) {
  const [mode, setMode] = useState(null);
  const [showMove, setShowMove] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    setMode(null);
    setShowMove(false);
    setShowCategories(false);
    setAttachments([]);
    if (message?.hasAttachments) {
      getAttachments(message.id).then(setAttachments).catch(() => {});
    }
  }, [message?.id]);

  if (!message) {
    return (
      <div className="email-view-empty">
        <div className="empty-icon">✉</div>
        <div>Select a message to read</div>
      </div>
    );
  }

  const isFlagged = message.flag?.flagStatus === 'flagged';
  const isComplete = message.flag?.flagStatus === 'complete';
  const isPinned = pinnedIds.includes(message.id);

  async function handleDelete() {
    try { await deleteMessage(message.id); onDeleted(message.id); } catch (e) { setError(e.message); }
  }

  async function handleMove(folderId) {
    try { await moveMessage(message.id, folderId); onMoved(message.id); setShowMove(false); } catch (e) { setError(e.message); }
  }

  async function handleFlag() {
    const newStatus = isFlagged ? 'notFlagged' : 'flagged';
    try {
      await flagMessage(message.id, newStatus);
      onMessageUpdate(message.id, { flag: { flagStatus: newStatus } });
    } catch (e) { setError(e.message); }
  }

  async function handleComplete() {
    const newStatus = isComplete ? 'notFlagged' : 'complete';
    try {
      await flagMessage(message.id, newStatus);
      onMessageUpdate(message.id, { flag: { flagStatus: newStatus } });
    } catch (e) { setError(e.message); }
  }

  async function handleMarkUnread() {
    try {
      await markRead(message.id, false);
      onMessageUpdate(message.id, { isRead: false });
    } catch (e) { setError(e.message); }
  }

  async function handleToggleCategory(catName) {
    const current = message.categories || [];
    const next = current.includes(catName)
      ? current.filter(c => c !== catName)
      : [...current, catName];
    try {
      await categorizeMessage(message.id, next);
      onMessageUpdate(message.id, { categories: next });
    } catch (e) { setError(e.message); }
  }

  const sender = message.from?.emailAddress;
  const initials = sender?.name
    ? sender.name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()
    : (sender?.address || '?')[0].toUpperCase();
  const hue = [...(sender?.name || sender?.address || '')].reduce((h, c) => h + c.charCodeAt(0), 0) % 360;

  return (
    <div className="email-view">
      {/* Toolbar */}
      <div className="email-view-toolbar">
        <button className="toolbar-btn" onClick={() => setMode('reply')} title="Reply">
          <span>↩</span> Reply
        </button>
        <button className="toolbar-btn" onClick={() => setMode('replyAll')} title="Reply All">
          <span>↩↩</span> Reply all
        </button>
        <button className="toolbar-btn" onClick={() => setMode('forward')} title="Forward">
          <span>↪</span> Forward
        </button>
        <div className="toolbar-divider" />
        <button className="toolbar-btn" onClick={handleDelete} title="Delete">🗑 Delete</button>
        <button className="toolbar-btn" onClick={() => setShowMove(v => !v)} title="Move to">
          📁 Move to {showMove ? '▴' : '▾'}
        </button>
        <button className="toolbar-btn" onClick={() => setShowCategories(v => !v)} title="Categorize">
          🏷 Categorize {showCategories ? '▴' : '▾'}
        </button>
        <div className="toolbar-divider" />
        <button className={`toolbar-btn ${isFlagged ? 'active-flag' : ''}`} onClick={handleFlag} title="Flag">
          🚩 {isFlagged ? 'Unflag' : 'Flag'}
        </button>
        <button className={`toolbar-btn ${isComplete ? 'active-complete' : ''}`} onClick={handleComplete} title="Mark complete">
          ✅ {isComplete ? 'Unmark' : 'Complete'}
        </button>
        <button className="toolbar-btn" onClick={handleMarkUnread} title="Mark as unread">
          📩 Unread
        </button>
        {onTogglePin && (
          <button className={`toolbar-btn ${isPinned ? 'active-flag' : ''}`} onClick={() => onTogglePin(message.id)} title={isPinned ? 'Unpin' : 'Pin'}>
            📌 {isPinned ? 'Unpin' : 'Pin'}
          </button>
        )}
        {message.importance === 'high' && (
          <span className="importance-badge high">! High importance</span>
        )}
      </div>

      {/* Dropdowns */}
      {showMove && (
        <div className="dropdown-panel">
          <div className="dropdown-title">Move to folder</div>
          <FolderPicker folders={folders} onPick={handleMove} />
        </div>
      )}

      {showCategories && (
        <div className="dropdown-panel">
          <div className="dropdown-title">Categorize</div>
          <div className="dropdown-items">
            {allCategories.map(cat => {
              const active = message.categories?.includes(cat.displayName);
              return (
                <button
                  key={cat.displayName}
                  className={active ? 'active-cat' : ''}
                  onClick={() => handleToggleCategory(cat.displayName)}
                >
                  <span className="cat-swatch" style={{ background: categoryMap?.[cat.displayName] || colorByName(cat.displayName) }} />
                  {cat.displayName}
                  {active && ' ✓'}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error && <div className="error-banner">{error} <button onClick={() => setError('')}>✕</button></div>}

      {/* Header */}
      <div className="email-view-header">
        <div className="email-view-avatar" style={{ background: `hsl(${hue},45%,48%)` }}>
          {initials}
        </div>
        <div className="email-view-meta">
          <div className="email-view-subject">{message.subject || '(no subject)'}</div>
          <div className="email-view-from">
            <strong>{sender?.name || sender?.address}</strong>
            {sender?.name && <span className="meta-email"> &lt;{sender.address}&gt;</span>}
          </div>
          <div className="email-view-details">
            <span>To: {message.toRecipients?.map(r => r.emailAddress.address).join(', ')}</span>
            {message.ccRecipients?.length > 0 && (
              <span> · Cc: {message.ccRecipients.map(r => r.emailAddress.address).join(', ')}</span>
            )}
            <span className="meta-date">{format(new Date(message.receivedDateTime), 'EEE, MMM d yyyy h:mm a')}</span>
          </div>
          {message.categories?.length > 0 && (
            <div className="email-view-cats">
              {message.categories.map(cat => (
                <span key={cat} className="category-chip" style={{ background: categoryMap?.[cat] || colorByName(cat) }}>{cat}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="attachment-bar">
          {attachments.map(a => (
            <div key={a.id} className="attachment-chip">
              <span className="attachment-icon">📎</span>
              <span className="attachment-name">{a.name}</span>
              <span className="attachment-size">({Math.round(a.size / 1024)}KB)</span>
            </div>
          ))}
        </div>
      )}

      {/* Body */}
      <div
        className="email-view-body"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.body?.content || message.bodyPreview || '') }}
      />

      {/* Inline compose */}
      {(mode === 'reply' || mode === 'replyAll' || mode === 'forward') && (
        <Compose
          mode={mode}
          originalMessage={message}
          signature={signature}
          onSend={async (payload) => {
            if (mode === 'forward') await forwardEmail(message.id, payload.to, payload.body);
            else await replyEmail(message.id, payload.body, mode === 'replyAll');
            setMode(null);
          }}
          onCancel={() => setMode(null)}
        />
      )}
    </div>
  );
}

function sanitizeHtml(html) {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
    .replace(/\son\w+="[^"]*"/gi, '')
    .replace(/\son\w+='[^']*'/gi, '');
}
